/* eslint-disable no-unused-vars */
import React, {
    useState
} from 'react';
import {
    useSearchParams
} from 'react-router-dom';
import {
    BookOpen, FolderOpen, Library, Plug, Radio, Server, Workflow
} from 'lucide-react';
import {
    cn
} from 'shared/lib/utils';
import DSOpCodesList from '../library/DSOpCodesList';
import DSInstructionsList from '../library/DSInstructionsList';
import DSApisList from '../library/DSApisList';
import DSMcpsList from '../library/DSMcpsList';
import DSChannelsList from '../library/DSChannelsList';
import DSCollectionsList from '../library/DSCollectionsList';
import './LibraryPage.css';

const LIBRARY_TABS = [
    {
        id: 'opcodes',
        label: 'OpCodes',
        icon: Workflow
    },
    {
        id: 'instructions',
        label: 'Instructions',
        icon: BookOpen
    },
    {
        id: 'apis',
        label: 'APIs',
        icon: Plug
    },
    {
        id: 'mcps',
        label: 'MCP Servers',
        icon: Server
    },
    {
        id: 'collections',
        label: 'Collections',
        icon: FolderOpen
    },
    {
        id: 'channels',
        label: 'Channels',
        icon: Radio
    },
];

const TAB_PANELS = {
    opcodes: DSOpCodesList,
    instructions: DSInstructionsList,
    apis: DSApisList,
    mcps: DSMcpsList,
    collections: DSCollectionsList,
    channels: DSChannelsList,
};

/**
 * LibraryPage — DE Studio → Library.
 */
export default function LibraryPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = searchParams.get('tab') || 'opcodes';
    const [mountedTabs, setMountedTabs] = useState(() => new Set([tab]));

    const setTab = (value) => {
        setSearchParams({ tab: value }, { replace: true });
        setMountedTabs((prev) => { prev.add(value); return new Set(prev); });
    };

    const ActivePanel = TAB_PANELS[tab] || DSOpCodesList;

    return (
        <div className="ds-hub-page" style={{
            padding: '20px 28px',
            minHeight: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
        }}>
            <div style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 16,
            }}>
                <div style={{
                    width: 34,
                    height: 34,
                    borderRadius: 'var(--ds-radius-sm)',
                    background: 'var(--ds-surface-2)',
                    color: 'var(--ds-text-muted)',
                    border: '1px solid var(--ds-border)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <Library size={16} />
                </div>
                <div style={{ minWidth: 0 }}>
                    <div style={{
                        fontSize: 'var(--ds-text-lg)',
                        fontWeight: 700,
                        color: 'var(--ds-text)',
                        letterSpacing: '-0.015em',
                        lineHeight: 1.2,
                    }}>
                        Library
                    </div>
                    <div style={{
                        fontSize: 'var(--ds-text-sm)',
                        color: 'var(--ds-text-muted)',
                        marginTop: 2,
                    }}>
                        Shared building blocks for your digital employees
                    </div>
                </div>
            </div>

            <div className="ds-lib-shell">
                <div className="ds-lib-body">
                    <nav className="ds-lib-sidebar" aria-label="Library sections">
                        {LIBRARY_TABS.map((item) => {
                            const Icon = item.icon;
                            const active = tab === item.id;
                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={cn('ds-lib-sidebar-item', active && 'ds-lib-sidebar-item--active')}
                                    aria-current={active ? 'page' : undefined}
                                    onClick={() => setTab(item.id)}
                                >
                                    <Icon size={16} aria-hidden="true" />
                                    {item.label}
                                </button>
                            );
                        })}
                    </nav>

                    <div className="ds-lib-main">
                        {LIBRARY_TABS.map((item) => {
                            if (!mountedTabs.has(item.id)) return null;
                            const Panel = TAB_PANELS[item.id];
                            return (
                                <div
                                    key={item.id}
                                    className={cn('ds-lib-panel', tab === item.id && 'ds-lib-panel--active')}
                                    hidden={tab !== item.id}
                                >
                                    <Panel />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
